import { ChangeEvent } from 'react';
import { Card } from '@nimbus-ds/components';
import {
  ProductRestrictionType,
  WeightRestrictionType,
  ValueRestrictionType,
} from '@tiendanube/common/src/enums';
import { Stack } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import ProductRestrictionSection from './components/ProductRestrictionSection';
import ValueRestrictionSection from './components/ValueRestrictionSection';
import WeightRestrictionSection from './components/WeightRestrictionSection';
import Skeleton from './Skeleton';
import {
  UpsellProtectionProvider,
  type UpsellProtectionConfig,
} from '../../../hooks';

interface ShippingRestrictionsCardProps {
  restrictedProducts: ProductRestrictionType;
  cartWeight: WeightRestrictionType;
  cartValue: ValueRestrictionType;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  restrictedCategories?: number[];
  restrictedProductIds?: string[];
  onCategoriesChange: (categories: number[]) => void;
  onProductsChange: (variantIds: string[]) => void;
  cartWeightMin?: string;
  cartWeightMax?: string;
  cartValueMin?: string;
  cartValueMax?: string;
  errors?: Partial<{
    cartWeightMin: string;
    cartWeightMax: string;
    cartValueMin: string;
    cartValueMax: string;
  }>;
  currencySymbol: string;
  upsellConfig?: UpsellProtectionConfig;
}

interface ShippingRestrictionsCardContentProps {
  restrictedProducts: ProductRestrictionType;
  cartWeight: WeightRestrictionType;
  cartValue: ValueRestrictionType;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  restrictedCategories: number[];
  restrictedProductIds: string[];
  onCategoriesChange: (categories: number[]) => void;
  onProductsChange: (variantIds: string[]) => void;
  cartWeightMin?: string;
  cartWeightMax?: string;
  cartValueMin?: string;
  cartValueMax?: string;
  errors: Partial<{
    cartWeightMin: string;
    cartWeightMax: string;
    cartValueMin: string;
    cartValueMax: string;
  }>;
  currencySymbol: string;
}

function ShippingRestrictionsCardContent({
  restrictedProducts,
  cartWeight,
  cartValue,
  onChange,
  restrictedCategories,
  restrictedProductIds,
  onCategoriesChange,
  onProductsChange,
  cartWeightMin,
  cartWeightMax,
  cartValueMin,
  cartValueMax,
  errors,
  currencySymbol,
}: Readonly<ShippingRestrictionsCardContentProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.restrictions',
  );

  return (
    <Card>
      <Card.Header title={t('title')} />
      <Card.Body>
        <Stack column align="stretch">
          <Stack.Item>
            <ProductRestrictionSection
              restrictedProducts={restrictedProducts}
              onChange={onChange}
              restrictedCategories={restrictedCategories}
              restrictedProductIds={restrictedProductIds}
              onCategoriesChange={onCategoriesChange}
              onProductsChange={onProductsChange}
            />
          </Stack.Item>

          <Stack.Item>
            <WeightRestrictionSection
              cartWeight={cartWeight}
              onChange={onChange}
              cartWeightMin={cartWeightMin}
              cartWeightMax={cartWeightMax}
              errors={{
                cartWeightMin: errors.cartWeightMin,
                cartWeightMax: errors.cartWeightMax,
              }}
            />
          </Stack.Item>

          <Stack.Item>
            <ValueRestrictionSection
              cartValue={cartValue}
              onChange={onChange}
              cartValueMin={cartValueMin}
              cartValueMax={cartValueMax}
              currencySymbol={currencySymbol}
              errors={{
                cartValueMin: errors.cartValueMin,
                cartValueMax: errors.cartValueMax,
              }}
            />
          </Stack.Item>
        </Stack>
      </Card.Body>
    </Card>
  );
}

function ShippingRestrictionsCard({
  restrictedProducts = ProductRestrictionType.NO_RESTRICTION,
  cartWeight = WeightRestrictionType.ALL_WEIGHTS,
  cartValue = ValueRestrictionType.ALL_VALUES,
  onChange,
  restrictedCategories = [],
  restrictedProductIds = [],
  onCategoriesChange,
  onProductsChange,
  cartWeightMin,
  cartWeightMax,
  cartValueMin,
  cartValueMax,
  errors = {},
  currencySymbol,
  upsellConfig,
}: Readonly<ShippingRestrictionsCardProps>): JSX.Element {
  if (upsellConfig) {
    return (
      <UpsellProtectionProvider
        enabled={upsellConfig.enabled}
        featureKey={upsellConfig.featureKey}
        trackingSource={upsellConfig.trackingSource}
        title={upsellConfig.title}
      >
        <ShippingRestrictionsCardContent
          restrictedProducts={restrictedProducts}
          cartWeight={cartWeight}
          cartValue={cartValue}
          onChange={onChange}
          restrictedCategories={restrictedCategories}
          restrictedProductIds={restrictedProductIds}
          onCategoriesChange={onCategoriesChange}
          onProductsChange={onProductsChange}
          cartWeightMin={cartWeightMin}
          cartWeightMax={cartWeightMax}
          cartValueMin={cartValueMin}
          cartValueMax={cartValueMax}
          errors={errors}
          currencySymbol={currencySymbol}
        />
      </UpsellProtectionProvider>
    );
  }

  return (
    <ShippingRestrictionsCardContent
      restrictedProducts={restrictedProducts}
      cartWeight={cartWeight}
      cartValue={cartValue}
      onChange={onChange}
      restrictedCategories={restrictedCategories}
      restrictedProductIds={restrictedProductIds}
      onCategoriesChange={onCategoriesChange}
      onProductsChange={onProductsChange}
      cartWeightMin={cartWeightMin}
      cartWeightMax={cartWeightMax}
      cartValueMin={cartValueMin}
      cartValueMax={cartValueMax}
      errors={errors}
      currencySymbol={currencySymbol}
    />
  );
}

ShippingRestrictionsCard.Skeleton = Skeleton;

export default ShippingRestrictionsCard;
